import { ReactNode, useEffect, useRef } from 'react';
import { View, ViewProps } from '@shared/components/View';
import { getController } from '@shared/util/timeout';
import styles from './AnchoredTooltip.module.css';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface AnchoredTooltipProps extends ViewProps {
  tooltip: ReactNode;
  position?: TooltipPosition;
}

const tooltipAnchorName = '--tooltip-anchor';

export function AnchoredTooltip({
  className,
  children,
  tooltip,
  position = 'top',
  ...rest
}: AnchoredTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const firstChild = ref.current?.firstChild;
    const tooltipEl = tooltipRef.current;
    if (!(firstChild instanceof HTMLElement) || !tooltipEl) {
      return;
    }

    const { signal, abort } = getController();
    const show = () => tooltipEl.showPopover();
    const hide = () => tooltipEl.hidePopover();

    firstChild.style.anchorName = tooltipAnchorName;
    firstChild.addEventListener('mouseenter', show, { signal });
    firstChild.addEventListener('mouseleave', hide, { signal });
    return abort;
  }, []);

  return (
    <View
      {...rest}
      ref={ref}
      className={[styles.anchoredTooltip, className]}
      data-position={position}
    >
      {children}
      {tooltip && (
        <div ref={tooltipRef} className={styles.tooltip} popover="manual">
          {tooltip}
        </div>
      )}
    </View>
  );
}
